import React from "react";
import { PriceAndPromotionInfo } from "shared/src/types/PriceAndPromotionInfo";
import { BettyBundle } from "shared/src/types/EvaluateArticleNew";
import { AmountsInBasket, PriceAndPromotionInfos } from "shared/src/types/ArticleCardProps";
import { sortBundleSelectorAndFilterNonSearchableBundles } from "../utils/articleUtils";
import { getPdpLink } from "shared/src/utils/articleUtil";

export function useSelectBundle(
    eventUuidGenerator: () => string,
    bundles?: Record<string, BettyBundle>,
    originalBundleSelector?: Record<string, string>,
    preselectedBundleId?: string | null,
    priceAndPromotionInfos?: PriceAndPromotionInfos,
    selectedStoreId?: string | null,
    isFsdShop?: boolean,
    isMarioCountry?: boolean,
    isEmployee?: boolean,
    amountsInBasket?: AmountsInBasket
) {
    const getBundleByBundleId = (bettyBundleId: string | null) => {
        if (bettyBundleId && bundles) {
            for (const bundleNumber in bundles) {
                const bundle = bundles[bundleNumber];
                if (bundle?.bundleId?.bettyBundleId === bettyBundleId) {
                    return bundle;
                }
            }
        }
        return null;
    };

    const getPriceAndPromotionInfoByBundleId = (bettyBundleId: string | null): PriceAndPromotionInfo | null => {
        if (bettyBundleId && priceAndPromotionInfos) {
            return priceAndPromotionInfos[bettyBundleId] || null;
        }
        return null;
    };

    const getInitialBundleId = (
        bundles: Record<string, BettyBundle> | undefined,
        bundleSelector: Record<string, string> | undefined,
        preselectedBundleId: string | null | undefined
    ): string | null => {
        if (bundles && bundleSelector) {
            for (const bundleNumber in bundleSelector) {
                const bundle = bundles[bundleNumber];
                const bundleId = bundle?.bundleId?.bettyBundleId;
                if (bundleId && (!preselectedBundleId || preselectedBundleId === bundleId)) {
                    return bundleId;
                }
            }
            // fallback to first existing bundle if preselected bundle was not found
            if (preselectedBundleId) {
                return getInitialBundleId(bundles, bundleSelector, undefined);
            }
        }
        return null;
    };

    const getStoreDataByBundleId = (bettyBundleId: string | null, storeId: string | undefined | null) => {
        const bundle = getBundleByBundleId(bettyBundleId);
        if (bundle?.stores && storeId) {
            return bundle.stores[storeId];
        }
        return null;
    };

    const getAmountInBasketByBundleId = (bettyBundleId: string | null) => {
        if (bettyBundleId && amountsInBasket) {
            return amountsInBasket[bettyBundleId] || null;
        }
        return null;
    };

    /**
     * Gets the amount in the DNR (Discount and Rebate) basket for a specific bundle.
     *
     * @param bettyBundleId - The ID of the bundle to check
     * @returns The current threshold value from applied DNRs if available,
     *          otherwise falls back to the amount in basket, or null if neither exists
     */
    const getAmountInDnrBasket = (bettyBundleId: string | null): number | null => {
        // Early return if no bundle ID is provided
        if (!bettyBundleId) {
            return null;
        }

        // Try to get the current threshold from the first applied DNR for this bundle
        const dnrThreshold = priceAndPromotionInfos?.[bettyBundleId]?.appliedDnrs?.[0]?.currentThreshold;

        // If a valid threshold exists, return it as a number
        if (dnrThreshold !== null && dnrThreshold !== undefined) {
            return parseFloat(dnrThreshold);
        }

        // Otherwise fall back to the amount in basket, or null if that doesn't exist
        return amountsInBasket?.[bettyBundleId] ?? null;
    };

    const [eventId, setEventId] = React.useState(eventUuidGenerator());
    const [sortedBundleSelector, setSortedBundleSelector] = React.useState(
        sortBundleSelectorAndFilterNonSearchableBundles(
            bundles,
            originalBundleSelector,
            selectedStoreId,
            preselectedBundleId,
            isFsdShop,
            isMarioCountry,
            isEmployee
        )
    );
    const [selectedBundleId, setSelectedBundleId] = React.useState(
        getInitialBundleId(bundles, sortedBundleSelector, preselectedBundleId)
    );
    const [selectedBundle, setSelectedBundle] = React.useState(getBundleByBundleId(selectedBundleId));
    const [selectedPriceAndPromotionInfo, setSelectedPrices] = React.useState(
        getPriceAndPromotionInfoByBundleId(selectedBundleId)
    );
    const [selectedStoreData, setSelectedStoreData] = React.useState(
        getStoreDataByBundleId(selectedBundleId, selectedStoreId)
    );
    const [selectedAmountInBasket, setSelectedAmountInBasket] = React.useState(
        getAmountInBasketByBundleId(selectedBundleId)
    );

    const [selectedAmountInDnr, setSelectedAmountInDnr] = React.useState(getAmountInDnrBasket(selectedBundleId));
    const [pdpLink, setPdpLink] = React.useState(getPdpLink(selectedBundle));

    React.useEffect(() => {
        const bundle = getBundleByBundleId(selectedBundleId);
        setSelectedBundle(bundle);
        setPdpLink(getPdpLink(bundle));
    }, [bundles]);

    React.useEffect(() => {
        setSelectedPrices(getPriceAndPromotionInfoByBundleId(selectedBundleId));
    }, [priceAndPromotionInfos]);

    React.useEffect(() => {
        setSelectedStoreData(getStoreDataByBundleId(selectedBundleId, selectedStoreId));
        setSortedBundleSelector(
            sortBundleSelectorAndFilterNonSearchableBundles(
                bundles,
                originalBundleSelector,
                selectedStoreId,
                selectedBundleId,
                isFsdShop,
                isMarioCountry,
                isEmployee
            )
        );
    }, [selectedStoreId, bundles]);

    React.useEffect(() => {
        setSelectedAmountInBasket(getAmountInBasketByBundleId(selectedBundleId));
        setSelectedAmountInDnr(getAmountInDnrBasket(selectedBundleId));
    }, [amountsInBasket]);

    const setChangedBundleId = (bettyBundleId: string) => {
        const bundle = getBundleByBundleId(bettyBundleId);
        if (bundle) {
            setSelectedPrices(getPriceAndPromotionInfoByBundleId(bettyBundleId));
            setSelectedBundleId(bettyBundleId);
            setEventId(eventUuidGenerator());
            setSelectedBundle(bundle);
            setSelectedStoreData(getStoreDataByBundleId(bettyBundleId, selectedStoreId));
            setSelectedAmountInBasket(getAmountInBasketByBundleId(bettyBundleId));
            setSelectedAmountInDnr(getAmountInDnrBasket(bettyBundleId));
            setPdpLink(getPdpLink(bundle));
        }
    };

    return {
        eventId,
        pdpLink,
        selectedBundle,
        selectedPriceAndPromotionInfo,
        selectedStoreData,
        selectedAmountInBasket,
        selectedAmountInDnr,
        sortedBundleSelector,
        setChangedBundleId
    };
}
